import { FC, useState } from 'react'

import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { isEqual, uniqWith } from 'lodash-es'

import {
  Avatar,
  AvatarFallback,
  Button,
  Cluster,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core/common'

import { useAuth } from '@/features/auth'
import { Document } from '@/features/documents/domain/documents'
import { isPreviewableExtension } from '@/utils/documentPreview'
import { getMicrosoftPreviewUrlIfApplicable } from '@/utils/documentPreviewUrl'
import { downloadBlob } from '@/utils/download'
import { getInitialsForName } from '@/utils/initials'
import { useLocalisation } from '@/utils/localisation'

import { DocumentUpload } from './upload'
import { localizeDocumentTypeLabel } from '../../../utils/localisation/documentTypes'
import { useEntityDocumentDelete } from '../api/useEntityDocumentDelete'
import { entityDocumentQueryOptions, entityDocumentsQueryOptions } from '../api/useEntityDocuments'

export type DocumentsListProps = { id: string; manualQuoteID?: string; bookingID?: string }

export const DocumentsList: FC<DocumentsListProps> = ({ id, manualQuoteID, bookingID }) => {
  const { translate, translateDate } = useLocalisation()
  const toaster = useToaster()
  const queryClient = useQueryClient()
  const [currentPreviewDocument, setCurrentPreviewDocument] = useState<Document | undefined>(undefined)
  const [currentPreviewDocumentUrl, setCurrentPreviewDocumentUrl] = useState<string | undefined>(undefined)
  const [currentDeleteDocument, setCurrentDeleteDocument] = useState<Document | undefined>(undefined)

  const { isCustomerActive } = useAuth()

  const deleteDocumentMutation = useEntityDocumentDelete()
  const { data: documents } = useSuspenseQuery(entityDocumentsQueryOptions(id, manualQuoteID, bookingID))
  const documentTypes = uniqWith(
    documents.results.map((document) => document.documentType).filter((documentType) => !!documentType),
    isEqual
  )

  const clusters = documentTypes.map((documentType) => ({
    code: documentType.code,
    name: localizeDocumentTypeLabel(translate, documentType.code, documentType.displayName),
    items: documents.results
      .filter((document) => document.documentTypeCode === documentType.code)
      .map((document) => ({
        ...document,
        updatedBy: {
          ...document.updatedBy,
          fullName: !document.updatedBy.id.startsWith('U-') ? 'Forto' : document.updatedBy.fullName,
        },
      })),
  }))

  const getDocumentBlob = async (document: Document): Promise<Blob> => {
    const data = await queryClient.query(entityDocumentQueryOptions(id, document.id, document.fileName))
    return data.blob
  }

  const getDocumentUrl = async (document: Document): Promise<string> => {
    const data = await getDocumentBlob(document)
    const url = URL.createObjectURL(new Blob([data], { type: document.mimeType }))
    return url
  }

  const handleDocumentPreview = async (document: Document) => {
    setCurrentPreviewDocument(document)

    const msftPreviewUrl = await getMicrosoftPreviewUrlIfApplicable(document.id, document.fileName)

    if (msftPreviewUrl) {
      setCurrentPreviewDocumentUrl(msftPreviewUrl)
    } else {
      const documentUrl = await getDocumentUrl(document)
      setCurrentPreviewDocumentUrl(documentUrl)
    }
  }

  return (
    <>
      <Stack gap={16}>
        <DocumentUpload id={id} isRoleRequired />
        <Stack gap={16}>
          {clusters
            .filter((cluster) => cluster.items.length)
            .map((cluster) => {
              return (
                <Stack key={cluster.code} gap={8}>
                  <Typography variant="body">{cluster.name}</Typography>
                  <Table
                    footless
                    columns={[
                      { accessorKey: 'fileName', header: translate('documents.table.name'), enableSorting: true },
                      { accessorKey: 'version', header: translate('documents.table.version'), enableSorting: true },
                      {
                        accessorKey: 'updatedAt',
                        header: translate('documents.table.date'),
                        enableSorting: true,
                        cell: ({ row }) => translateDate('shipment_documents_date', new Date(row.original.updatedAt)),
                      },
                      {
                        accessorKey: 'updatedBy',
                        header: translate('documents.table.uploaded'),
                        enableSorting: true,
                        cell: ({ row }) => (
                          <Cluster gap={8} y="center">
                            <Avatar size="sm">
                              <AvatarFallback>{getInitialsForName(row.original.updatedBy.fullName)}</AvatarFallback>
                            </Avatar>
                            {row.original.updatedBy.fullName}
                          </Cluster>
                        ),
                      },
                      {
                        id: 'actions',
                        header: '',
                        enableSorting: false,
                        cell: ({ row }) => {
                          const document = row.original
                          return (
                            <Cluster gap={4} x="end">
                              {document.fileName &&
                              (() => {
                                const extension = document.fileName.split('.').pop()?.toLowerCase() || ''
                                return isPreviewableExtension(extension)
                              })() ? (
                                <IconButton
                                  icon="View"
                                  label={translate('actions.view')}
                                  onClick={() => handleDocumentPreview(document)}
                                />
                              ) : null}
                              <IconButton
                                icon="Download"
                                label={translate('actions.download')}
                                onClick={async () => {
                                  const blob = await getDocumentBlob(document)
                                  downloadBlob(blob, document.fileName)
                                }}
                              />
                              {document.isDeletable && isCustomerActive ? (
                                <IconButton
                                  icon="Bin"
                                  label={translate('actions.delete')}
                                  color="negative"
                                  onClick={() => setCurrentDeleteDocument(document)}
                                />
                              ) : null}
                            </Cluster>
                          )
                        },
                      },
                    ]}
                    data={cluster.items}
                  />
                </Stack>
              )
            })}
        </Stack>
      </Stack>

      {currentPreviewDocument && currentPreviewDocumentUrl ? (
        <Dialog
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setCurrentPreviewDocument(undefined)
              setCurrentPreviewDocumentUrl(undefined)
            }
          }}
        >
          <DialogContent maxWidth="full" maxHeight="full">
            <DialogHeader>
              <DialogTitle>{translate('documents.preview.title')}</DialogTitle>
            </DialogHeader>
            <iframe
              src={currentPreviewDocumentUrl}
              width="100%"
              // eslint-disable-next-line react/forbid-dom-props
              style={{ height: '95vh' }}
            ></iframe>
          </DialogContent>
        </Dialog>
      ) : null}

      {currentDeleteDocument ? (
        <Dialog
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setCurrentDeleteDocument(undefined)
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{translate('documents.delete.title')}</DialogTitle>
            </DialogHeader>
            <Typography>{translate('documents.delete.message', { name: currentDeleteDocument.fileName })}</Typography>
            <DialogFooter>
              <Button variant="outline-primary" onClick={() => setCurrentDeleteDocument(undefined)}>
                {translate('documents.delete.actions.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  await deleteDocumentMutation.mutateAsync({
                    entityId: id,
                    documentId: currentDeleteDocument.id,
                  })
                  toaster({ title: translate('documents.delete.notification.deleted') })
                  setCurrentDeleteDocument(undefined)
                }}
              >
                {translate('documents.delete.actions.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  )
}
